/** A message box where each person's first name sits as a chip, never as raw "{first name}". The value keeps the token. */
import { useEffect, useRef } from "react";

const TOKEN = "{first name}";

function build(value: string): Node[] {
  return value.split(TOKEN).flatMap((text, i) => {
    const chip = document.createElement("span");
    chip.className = "mtok";
    chip.contentEditable = "false";
    chip.dataset.token = "1";
    chip.textContent = "first name";
    return i === 0 ? [document.createTextNode(text)] : [chip, document.createTextNode(text)];
  });
}

/** What the box holds, as text: a chip is the token, a new line is a new line. */
function read(node: Node, out: { text: string }): void {
  node.childNodes.forEach((child, i) => {
    if (child.nodeType === Node.TEXT_NODE) out.text += child.textContent ?? "";
    else if (child instanceof HTMLElement && child.dataset.token) out.text += TOKEN;
    else if (child instanceof HTMLBRElement) out.text += "\n";
    else if (child instanceof HTMLElement) {
      if (i > 0 && !out.text.endsWith("\n")) out.text += "\n";
      read(child, out);
    }
  });
}

export function MergeBox({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (text: string) => void }) {
  const box = useRef<HTMLDivElement>(null);
  // Why filled once: the box is typed into directly, and writing it again on every keystroke would move the cursor.
  useEffect(() => {
    box.current?.replaceChildren(...build(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      id={id}
      ref={box}
      className="inp mbox"
      role="textbox"
      aria-multiline="true"
      aria-label={label}
      contentEditable
      suppressContentEditableWarning
      onInput={(e) => {
        const out = { text: "" };
        read(e.currentTarget, out);
        onChange(out.text);
      }}
    />
  );
}
